import { ComponentRef, Injectable, Type, ViewContainerRef, isSignal } from '@angular/core';

/**
 * Hosts a single dynamically-created modal component outside the virtualized list, so that
 * fixed-position backdrops aren't clipped by cdk-virtual-scroll-viewport's transform/contain
 * containing block, and so open modals survive their triggering row being destroyed by virtualization.
 */
@Injectable()
export class ModalLauncherService {
  private viewContainerRef?: ViewContainerRef;
  private currentOwner?: unknown;
  private openCount = 0;

  /**
   * Increments on every open(). Callers that open across an async gap capture this before the gap
   * and re-check it on resolve, so a late response cannot replace a modal opened while it was in
   * flight. currentOwner can't answer that question: nothing tells this service when a modal is
   * closed, so it stays set for a modal the user has already dismissed.
   */
  get generation(): number {
    return this.openCount;
  }

  registerOutlet(viewContainerRef: ViewContainerRef): void {
    this.viewContainerRef = viewContainerRef;
  }

  // Value types are intentionally loose here, matching how Angular checks template property
  // bindings without `strictTemplates` enabled (property names are checked, value types are not).
  open<T>(component: Type<T>, inputs: { [K in keyof T]?: any }, owner: unknown): ComponentRef<T> {
    this.viewContainerRef.clear();
    const ref = this.viewContainerRef.createComponent(component);
    this.applyInputs(ref, inputs);
    // Every modal reaches its inner app-modal through a non-static @ViewChild, which stays
    // undefined until the created view is change-detected. Callers call show() synchronously on
    // the returned ref, so that first pass has to happen here. Inputs are assigned above so this
    // pass renders with them already in place.
    ref.changeDetectorRef.detectChanges();
    this.currentOwner = owner;
    this.openCount++;
    return this.withAutoDetectChanges(ref);
  }

  /**
   * Sets the caller's inputs on a freshly created modal, routing SIGNAL inputs through setInput().
   *
   * This was a flat Object.assign until the bid list started launching BidModalRefactorComponent,
   * which is partially migrated: `tenantName`, `modalId`, `buyerAuctionItemStatus` and
   * `auctionItemFee` are `input()` signals while the rest are still `@Input()` fields. Assigning a
   * signal input as a plain property REPLACES the InputSignal function with the raw value, so every
   * `modalId()` read in the template then throws "modalId is not a function" -- and it fails at
   * render time inside a dynamically created component, nowhere near this line. Nothing warns,
   * because a component instance is an ordinary object.
   *
   * isSignal() rather than reading the component def's input flags: it is public API, and it asks
   * the question that actually matters (is the property I am about to overwrite a signal?) instead
   * of re-deriving Angular's metadata. A plain @Input holds undefined or a value at this point, so
   * the two cases cannot be confused.
   *
   * Object.assign's tolerance is preserved for everything else ON PURPOSE. setInput() throws NG0303
   * for a property that is not a declared input, and callers here pass plain fields as well as
   * inputs; routing everything through setInput would turn those into runtime errors.
   */
  private applyInputs<T>(ref: ComponentRef<T>, inputs: { [K in keyof T]?: any }): void {
    for (const [name, value] of Object.entries(inputs)) {
      if (isSignal((ref.instance as Record<string, unknown>)[name])) {
        ref.setInput(name, value);
      } else {
        (ref.instance as Record<string, unknown>)[name] = value;
      }
    }
  }

  clearIfOwnedBy(owner: unknown): void {
    if (this.currentOwner === owner) {
      this.viewContainerRef?.clear();
      this.currentOwner = undefined;
    }
  }

  /**
   * Callers mutate `ref.instance` from async callbacks (HTTP success/error, etc.) that have no
   * relation to any Angular template event, so nothing marks the OnPush host of this outlet dirty
   * and the mutation never renders. Wrapping `.instance` so every external property set or method
   * call re-triggers change detection on this component's own view means callers can't forget to
   * do it themselves — this only reaches top-level property sets/method calls on the instance
   * itself, not mutations of nested objects reached through it.
   */
  private withAutoDetectChanges<T>(ref: ComponentRef<T>): ComponentRef<T> {
    const cdr = ref.changeDetectorRef;
    const detectingInstance = new Proxy(ref.instance as object, {
      set(target, prop, value) {
        (target as Record<PropertyKey, unknown>)[prop] = value;
        cdr.detectChanges();
        return true;
      },
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value !== 'function') {
          return value;
        }
        return (...args: unknown[]) => {
          const result = value.apply(target, args);
          cdr.detectChanges();
          return result;
        };
      }
    }) as T;

    return new Proxy(ref, {
      get(target, prop, receiver) {
        return prop === 'instance' ? detectingInstance : Reflect.get(target, prop, receiver);
      }
    });
  }
}
